import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Card, CardMedia, CardContent,
  IconButton, Button, CircularProgress, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Chip, Paper, Avatar,
  alpha, Grid,
} from '@mui/material';
import {
  Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon,
  PhotoLibrary as GalleryIcon,
  GridView as GridViewIcon, ViewList as ListViewIcon,
  Public as PublicIcon, EmojiEvents as AchievementIcon, Lock as LockIcon,
  AddPhotoAlternate as AddPhotoIcon, Visibility as VisibilityIcon,
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import ListFilterBar from '../../common/ListFilterBar';
import StatCards from '../../common/StatCards';
import Loading from '../../common/Loading';
import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import Header from '../../common/Header';
import FeedbackMessage from '../../common/FeedbackMessage';
import { ACCENT, BG_BOTTOM, BG_TOP, CARD, SUCCESS, TEXT_SECONDARY, TEXT_PRIMARY, ACTION_VIEW, ACTION_EDIT, ACTION_DELETE, TABLE_SURFACE_2, glassCardSx, RADIUS_MD, playfulBorder } from '../../common/SaaSTokens';
import TableList, { TableRowActions, TableListPagination } from '../../common/TableList';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import {
  canManageGallery,
  canAddPicturesToPrivateAccess,
  canEditGallery,
  canDeleteGallery,
  canViewGallery,
} from '../../../utils/galleryPermissions';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { GALLERY_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';
import {
  GALLERY_PUBLICITY_FILTER_OPTIONS,
  getGalleryPublicityLabel,
} from '../../../constants/galleryPublicity';
import StorageImage from '../../common/StorageImage';

// ─── Design tokens ────────────────────────────────────────────────────────────
const B = { 600: '#2563EB', 700: '#1D4ED8', 900: '#1E3A8A' };
const NEON  = ACCENT;
const NEON2 = TEXT_SECONDARY;
const NEON3 = SUCCESS;
const SURFACE_BASE = BG_BOTTOM;
const SURFACE_MID  = BG_TOP;
const SURFACE_UP   = CARD;

const DOT_GRID = 'none';
const SCAN_LINES = 'none';
const GRID_TITLE_TEXT = alpha('#F8FAFC', 0.95);

const GL_CSS = `
  @keyframes bar-sweep { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
  @keyframes gl-pulse  { 0%,100%{opacity:1} 50%{opacity:0.35} }
  @keyframes neon-ping { 0%{transform:scale(.8);opacity:.9} 100%{transform:scale(2.2);opacity:0} }
`;

// ─── Neon chip helpers ────────────────────────────────────────────────────────
const PUBLICITY_NEON = { public: NEON3, private_view: NEON, private_edit: NEON2, private_access: '#F59E0B' };

const NeonChip = ({ label, neon = NEON, size = 'small' }) => (
  <Chip
    label={label}
    size={size}
    sx={{
      fontFamily: "'DM Sans', sans-serif", fontSize: '0.7rem', fontWeight: 600, letterSpacing: 0.2,
      height: 22,
      background: alpha(neon, 0.12),
      color: neon, border: `1px solid ${alpha(neon, 0.32)}`,
      '& .MuiChip-label': { px: 1 },
    }}
  />
);

// ─── Shared card shell ────────────────────────────────────────────────────────
const cardShellSx = {
  ...glassCardSx,
  overflow: 'hidden',
  position: 'relative',
};

// ─────────────────────────────────────────────────────────────────────────────
const GalleryList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: currentUser } = useAuth();
  const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  const [images,                   setImages]                   = useState([]);
  const [loading,                  setLoading]                  = useState(true);
  const [error,                    setError]                    = useState(null);
  const [successMessage,           setSuccessMessage]           = useState(null);
  const [page,                     setPage]                     = useState(0);
  const [rowsPerPage,              setRowsPerPage]              = useState(12);
  const [searchTerm,               setSearchTerm]               = useState('');
  const [showFilters,              setShowFilters]              = useState(false);
  const [filters,                  setFilters]                  = useState({});
  const [activeStatFilter,         setActiveStatFilter]         = useState('all');
  const [viewMode,                 setViewMode]                 = useState('list');
  const [deleteDialogOpen,         setDeleteDialogOpen]         = useState(false);
  const [selectedImage,            setSelectedImage]            = useState(null);
  const [addPicturesDialogOpen,    setAddPicturesDialogOpen]    = useState(false);
  const [selectedGalleryForPics,   setSelectedGalleryForPics]   = useState(null);
  const [newPictures,              setNewPictures]              = useState([]);
  const [picturePreviews,          setPicturePreviews]          = useState([]);
  const [uploadLoading,            setUploadLoading]            = useState(false);
  const [addPicturesDragging,      setAddPicturesDragging]      = useState(false);

  const canManageAllGalleries   = canManageGallery(currentUser);
  const canCreateGallery        = canManageAllGalleries;
  const canAddPicturesToPrivate = (image) => canAddPicturesToPrivateAccess(currentUser, image);

  useEffect(() => {
    const cssId = 'gallerylist-ds-css';
    if (!document.getElementById(cssId)) {
      const el = document.createElement('style');
      el.id = cssId; el.innerHTML = GL_CSS;
      document.head.appendChild(el);
    }
  }, []);

  const filterOptions = [
    {
      name: 'publicity',
      label: 'Visibility',
      type: 'select',
      options: GALLERY_PUBLICITY_FILTER_OPTIONS,
    },
    {
      name: 'achievement',
      label: 'Gallery Type',
      type: 'select',
      options: [
        { value: 'true', label: 'Achievements' },
        { value: 'false', label: 'Regular' },
      ],
    },
    {
      name: 'achievement_type',
      label: 'Achievement Type',
      type: 'select',
      options: [
        { value: 'domestic', label: 'Domestic' },
        { value: 'international', label: 'International' },
      ],
    },
  ];

  useEffect(() => {
    if (!currentUser) { navigate('/login'); return; }
    fetchImages();
  }, [currentUser, location.state?.refreshList]);

  useEffect(() => {
    if (location.state?.successMessage) {
      setSuccessMessage(location.state.successMessage);
    }
  }, [location.state?.successMessage]);

  const fetchImages = async () => {
    try {
      setLoading(true);
      const response = await api.get('/galleries');
      setImages(response.data); setError(null);
    } catch { setError('Failed to load gallery images. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleDelete   = (image) => { setSelectedImage(image); setDeleteDialogOpen(true); };
  const confirmDelete  = async () => {
    try {
      const deletedName = getGalleryDisplayName(selectedImage);
      await api.delete(`/galleries/${selectedImage.id}`);
      setImages(images.filter(img => img.id !== selectedImage.id));
      setDeleteDialogOpen(false);
      setSelectedImage(null);
      setSuccessMessage(`Gallery "${deletedName}" has been deleted successfully!`);
    }
    catch { setError('Failed to delete image. Please try again.'); }
  };
  const handleChangePage        = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    if (newFilters.publicity === 'public') setActiveStatFilter('public');
    else if (newFilters.achievement === 'true') setActiveStatFilter('achievement');
    else if (newFilters.visibility_group === 'non_public') setActiveStatFilter('private');
    else setActiveStatFilter('all');
    setPage(0);
  };
  const handleClearFilters = () => { setFilters({}); setActiveStatFilter('all'); setSearchTerm(''); setPage(0); };
  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'public', 'achievement', 'private'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);
    const nextFilters = { ...filters };
    delete nextFilters.publicity;
    delete nextFilters.achievement;
    delete nextFilters.achievement_type;
    delete nextFilters.visibility_group;
    if (nextFilter === 'public') {
      nextFilters.publicity = 'public';
    } else if (nextFilter === 'achievement') {
      nextFilters.achievement = 'true';
    } else if (nextFilter === 'private') {
      nextFilters.visibility_group = 'non_public';
    }
    setFilters(nextFilters);
    setPage(0);
  };
  const handleAddPictures = (gallery) => {
    setSelectedGalleryForPics(gallery);
    setAddPicturesDialogOpen(true);
    setNewPictures([]);
    setPicturePreviews([]);
  };

  const appendPictureFiles = (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith('image/'));
    if (!files.length) return;
    setNewPictures((prev) => [...prev, ...files]);
    setPicturePreviews((prev) => [
      ...prev,
      ...files.map((file) => ({ file, url: URL.createObjectURL(file) })),
    ]);
  };

  const handlePictureFileChange = (event) => {
    appendPictureFiles(event.target.files);
    event.target.value = '';
  };

  const handleAddPicturesDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    setAddPicturesDragging(true);
  };

  const handleAddPicturesDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setAddPicturesDragging(false);
  };

  const handleAddPicturesDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setAddPicturesDragging(false);
    appendPictureFiles(e.dataTransfer?.files);
  };

  const removePendingPicture = (index) => {
    setPicturePreviews(prev => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed?.url) URL.revokeObjectURL(removed.url);
      return next;
    });
    setNewPictures(prev => prev.filter((_, i) => i !== index));
  };

  const closeAddPicturesDialog = () => {
    picturePreviews.forEach(p => { if (p.url) URL.revokeObjectURL(p.url); });
    setPicturePreviews([]);
    setNewPictures([]);
    setAddPicturesDragging(false);
    setAddPicturesDialogOpen(false);
    setSelectedGalleryForPics(null);
  };

  const handleUploadPictures = async () => {
    if (!newPictures.length || !selectedGalleryForPics) return;
    try {
      setUploadLoading(true);
      const targetGalleryName = getGalleryDisplayName(selectedGalleryForPics);
      const uploadedCount = newPictures.length;
      const fd = new FormData();
      newPictures.forEach((file, index) => fd.append(`other_pictures[${index}]`, file));
      await api.post(`/galleries/${selectedGalleryForPics.id}/add-pictures`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await fetchImages();
      closeAddPicturesDialog();
      setSuccessMessage(`${uploadedCount} picture${uploadedCount > 1 ? 's' : ''} added to "${targetGalleryName}" successfully!`);
    } catch { setError('Failed to upload pictures. Please try again.'); }
    finally { setUploadLoading(false); }
  };

  const getGalleryDisplayName = (image) => image?.event_name || image?.event?.name || image?.title || 'Untitled Gallery';
  const normalizeValue = (value) => String(value ?? '').trim().toLowerCase();
  const isAchievementImage = (image) => (
    image?.is_achievement === true
    || image?.is_achievement === 1
    || image?.is_achievement === '1'
    || normalizeValue(image?.is_achievement) === 'true'
  );
  const getPublicity = (image) => normalizeValue(image?.publicity);
  const getPublicityLabel = (image) => getGalleryPublicityLabel(getPublicity(image));
  const getImagePath = (image) => (
    image?.representative_picture
    || image?.image_path
    || image?.representativePicture
    || ''
  );

  const visibleImages = images.filter((image) => canViewGallery(currentUser, image));

  const filteredImages = visibleImages.filter(image => {
    const displayName = getGalleryDisplayName(image);
    const matchesSearch = displayName.toLowerCase().includes(searchTerm.toLowerCase()) || (image.achievement_description?.toLowerCase().includes(searchTerm.toLowerCase()) || '');
    const matchesFilters = Object.entries(filters).every(([key, value]) => {
      if (!value) return true;
      if (key === 'publicity') return getPublicity(image) === normalizeValue(value);
      if (key === 'visibility_group' && value === 'non_public') return getPublicity(image) !== 'public';
      if (key === 'achievement') {
        if (value === 'true') return isAchievementImage(image);
        if (value === 'false') return !isAchievementImage(image);
      }
      if (key === 'achievement_type') return normalizeValue(image?.achievement_type) === normalizeValue(value);
      return true;
    });
    return matchesSearch && matchesFilters;
  });

  const statsData = [
    { icon: GalleryIcon, value: visibleImages.length, label: 'Total Galleries', color: 'info' },
    { icon: PublicIcon, value: visibleImages.filter(i => getPublicity(i) === 'public').length, label: 'Public', color: 'success' },
    { icon: AchievementIcon, value: visibleImages.filter(isAchievementImage).length, label: 'Achievements', color: 'warning' },
    { icon: LockIcon, value: visibleImages.filter(i => getPublicity(i) !== 'public').length, label: 'Private', color: 'primary' },
  ];

  const activeStatIndex =
    activeStatFilter === 'public' ? 1 :
    activeStatFilter === 'achievement' ? 2 :
    activeStatFilter === 'private' ? 3 :
    0;

  const galleryColumns = useMemo(() => [
    {
      key: 'cover',
      label: 'Cover',
      sortable: false,
      minWidth: 88,
      render: (image) => (
        <Box sx={{ borderRadius: RADIUS_MD, overflow: 'hidden', width: 56, height: 56, border: playfulBorder(ACCENT, 0.2) }}>
          <StorageImage storagePath={getImagePath(image)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} sx={{ width: '100%', height: '100%' }} />
        </Box>
      ),
    },
    {
      key: 'name',
      label: 'Event / Title',
      primary: true,
      minWidth: 220,
      sortAccessor: (image) => getGalleryDisplayName(image),
      render: (image) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{
            fontFamily: "'DM Sans', sans-serif", fontWeight: 600,
            fontSize: '0.875rem', color: TEXT_PRIMARY,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {getGalleryDisplayName(image)}
          </Typography>
          {isAchievementImage(image) && image.achievement_type && (
            <Box sx={{ mt: 0.5 }}>
              <NeonChip label={image.achievement_type} neon="#F59E0B" />
            </Box>
          )}
        </Box>
      ),
    },
    {
      key: 'publicity',
      label: 'Visibility',
      minWidth: 140,
      sortAccessor: (image) => getPublicityLabel(image),
      render: (image) => (
        <NeonChip label={getPublicityLabel(image)} neon={PUBLICITY_NEON[getPublicity(image)] || alpha(ACCENT, 0.4)} />
      ),
    },
    {
      key: 'type',
      label: 'Type',
      minWidth: 120,
      sortAccessor: (image) => (isAchievementImage(image) ? 'achievement' : 'regular'),
      render: (image) => (
        isAchievementImage(image)
          ? <NeonChip label="Achievement" neon="#F59E0B" />
          : <NeonChip label="Regular" neon={alpha(ACCENT, 0.4)} />
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 160,
      render: (image) => (
        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
          {canAddPicturesToPrivate(image) && (
            <Tooltip title="Add Pictures" arrow>
              <IconButton
                size="small"
                data-action="edit"
                onClick={(e) => { e.stopPropagation(); handleAddPictures(image); }}
                sx={{
                  width: 28, height: 28, borderRadius: '8px', color: TEXT_SECONDARY,
                  '&:hover': { color: `${TEXT_SECONDARY} !important`, backgroundColor: `${alpha(TEXT_SECONDARY, 0.14)} !important` },
                }}
              >
                <AddPhotoIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <TableRowActions
            onView={(e) => { e?.stopPropagation?.(); navigate(`/galleries/${image.id}`); }}
            onEdit={canEditGallery(currentUser) ? (e) => { e?.stopPropagation?.(); navigate(`/galleries/edit/${image.id}`, { state: { from: '/galleries' } }); } : undefined}
            onDelete={canDeleteGallery(currentUser) ? (e) => { e?.stopPropagation?.(); handleDelete(image); } : undefined}
            showEdit={canEditGallery(currentUser)}
            showDelete={canDeleteGallery(currentUser)}
            viewTitle="View gallery"
            editTitle="Edit gallery"
            deleteTitle="Delete gallery"
          />
        </Box>
      ),
    },
  ], [currentUser, navigate]);

  // ── Action icon button helper ─────────────────────────────────────────────
  const ActionBtn = ({ title, icon: Icon, color, onClick }) => (
    <Tooltip title={title} arrow>
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onClick(e);
        }}
        sx={{
          width: 28,
          height: 28,
          borderRadius: '8px',
          color,
          transition: 'all 0.16s ease',
          '&:hover': {
            color: `${color} !important`,
            backgroundColor: `${alpha(color, 0.14)} !important`,
          },
        }}
      >
        <Icon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  if (!currentUser) return null;
  if (loading) return (
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading gallery images" size={56} variant="primary" />
    </Box>
  );

  // ── Shared dialog shell ───────────────────────────────────────────────────
  const DialogShell = ({ open, onClose, children }) => (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ elevation: 0, sx: { borderRadius: '16px', background: `linear-gradient(160deg, ${alpha(SURFACE_BASE, 0.98)} 0%, ${alpha(SURFACE_MID, 0.96)} 100%)`, border: `1px solid ${alpha(NEON, 0.17)}`, boxShadow: `0 24px 64px ${alpha('#000', 0.6)}, inset 0 1px 0 ${alpha(NEON, 0.1)}`, backdropFilter: 'blur(24px)', overflow: 'hidden', position: 'relative' } }}
      BackdropProps={{ sx: { background: 'rgba(3,9,18,0.72)', backdropFilter: 'blur(4px)' } }}
    >
      <Box sx={{ position: 'absolute', inset: 0, background: DOT_GRID, pointerEvents: 'none', zIndex: 0 }} />
      <Box sx={{ height: '2px', position: 'relative', zIndex: 2, background: `linear-gradient(90deg, transparent, ${alpha(NEON, 0.8)} 30%, ${alpha(NEON2, 0.9)} 50%, ${alpha(NEON, 0.8)} 70%, transparent)`, backgroundSize: '200% 100%', animation: 'bar-sweep 5s linear infinite' }} />
      {children}
    </Dialog>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 6 }}>

      <Header
        title="Gallery Management"
        subtitle="Manage and organize your image gallery collection"
        icon={GalleryIcon}
        actionButton={(
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {[{ mode: 'grid', Icon: GridViewIcon }, { mode: 'list', Icon: ListViewIcon }].map(({ mode, Icon }) => (
              <Tooltip key={mode} title={`${mode.charAt(0).toUpperCase() + mode.slice(1)} View`}>
                <IconButton onClick={() => setViewMode(mode)}
                  sx={{
                    borderRadius: '9px', width: 36, height: 36,
                    background: viewMode === mode ? alpha(ACCENT, 0.16) : alpha(TABLE_SURFACE_2, 0.5),
                    border: `1px solid ${alpha(ACCENT, viewMode === mode ? 0.4 : 0.15)}`,
                    color: viewMode === mode ? ACCENT : alpha(ACCENT, 0.5),
                    transition: 'all 0.2s ease',
                    '&:hover': { background: alpha(ACCENT, 0.1), borderColor: alpha(ACCENT, 0.35), color: ACCENT },
                  }}
                >
                  <Icon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            ))}
            {canCreateGallery && (
              <Button
                variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/galleries/add', { state: { from: '/galleries' } })}
                sx={{
                  borderRadius: '10px', px: 3, py: 1,
                  fontFamily: "'DM Sans', sans-serif", fontWeight: 700, fontSize: '0.875rem',
                  background: alpha(NEON, 0.16),
                  border: `1px solid ${alpha(NEON, 0.4)}`, color: NEON,
                  transition: 'all 0.22s ease',
                  '&:hover': { background: alpha(NEON, 0.22), color: '#fff', transform: 'translateY(-1px)' },
                }}
              >
                Add Gallery
              </Button>
            )}
          </Box>
        )}
      />

      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{ mb: 3, borderRadius: '12px', fontFamily: "'DM Sans', sans-serif", background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
      />
      <FeedbackMessage
        message={successMessage}
        severity="success"
        onClose={() => setSuccessMessage(null)}
        sx={{ mb: 3, borderRadius: '12px', fontFamily: "'DM Sans', sans-serif", fontWeight: 500, background: alpha(NEON3, 0.12), border: `1px solid ${alpha(NEON3, 0.25)}`, color: alpha(NEON3, 0.95) }}
      />

      <StatCards
        stats={statsData}
        onCardClick={handleStatCardClick}
        activeIndex={activeStatIndex}
      />

      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        filterOptions={filterOptions}
        searchPlaceholder="Search by event name or achievement description…"
      />

      {viewMode === 'list' && (
        <ListBulkToolbar
          config={GALLERY_BULK_CONFIG}
          currentUser={currentUser}
          selectedIds={selectedIds}
          onClearSelection={clearSelection}
          {...createBulkHandlers({
            endpoint: GALLERY_BULK_CONFIG.endpoint,
            setItems: setImages,
            selectedIds,
            clearSelection,
            setSuccess: setSuccessMessage,
            successLabel: 'gallery item(s)',
            refetch: fetchImages,
            setError,
          })}
        />
      )}

      {/* ── Grid View ── */}
      {viewMode === 'grid' && (
        filteredImages.length === 0 ? (
          <Card sx={cardShellSx}>
            <Box sx={{ position: 'absolute', inset: 0, background: DOT_GRID, pointerEvents: 'none', zIndex: 0 }} />
            <CardContent sx={{ py: 8, textAlign: 'center', position: 'relative', zIndex: 1 }}>
              <GalleryIcon sx={{ fontSize: 64, color: alpha(NEON, 0.18), mb: 2 }} />
              <Typography sx={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 700, fontSize: '1.1rem', color: alpha('#E2E8F0', 0.7), mb: 1 }}>
                {searchTerm || Object.keys(filters).length > 0 ? 'No galleries match your filters' : 'No galleries yet'}
              </Typography>
              <Typography sx={{ fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', color: alpha(NEON, 0.38) }}>
                {searchTerm || Object.keys(filters).length > 0 ? 'Try adjusting your search or filter criteria' : 'Start by adding a new gallery'}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            <Grid container spacing={2}>
              {filteredImages.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((image, idx) => (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={image.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.28, delay: idx * 0.04, ease: [0.4, 0, 0.2, 1] }}
                    style={{ width: '100%' }}
                  >
                    <Card
                      onClick={() => navigate(`/galleries/${image.id}`)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/galleries/${image.id}`);
                        }
                      }}
                      sx={{
                        aspectRatio: '1 / 1',
                        borderRadius: '14px',
                        overflow: 'hidden',
                        width: '100%',
                        position: 'relative',
                        background: SURFACE_BASE,
                        border: `1px solid ${alpha(NEON, 0.12)}`,
                        boxShadow: `0 4px 20px ${alpha('#000', 0.45)}`,
                        transition: 'all 0.28s ease',
                        cursor: 'pointer',
                        '&:hover': {
                          border: `1px solid ${alpha(NEON, 0.24)}`,
                          boxShadow: `0 8px 28px ${alpha('#000', 0.55)}`,
                          transform: 'translateY(-2px)',
                          '& .gallery-cover-img': { transform: 'scale(1.04)' },
                          '& .gallery-footer': { background: alpha(SURFACE_BASE, 0.98) },
                        },
                      }}
                    >
                      <StorageImage
                        className="gallery-cover-img"
                        storagePath={getImagePath(image)}
                        alt={getGalleryDisplayName(image)}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          filter: 'brightness(0.92) contrast(1.04) saturate(1.02)',
                          transition: 'transform 0.35s ease',
                        }}
                      />

                      <Box
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          zIndex: 1,
                          background: `linear-gradient(to bottom, ${alpha('#000', 0.35)} 0%, transparent 28%, transparent 52%, ${alpha(SURFACE_BASE, 0.88)} 78%, ${SURFACE_BASE} 100%)`,
                          pointerEvents: 'none',
                        }}
                      />

                      {image.is_achievement && (
                        <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}>
                          <NeonChip label={image.achievement_type || 'Achievement'} neon="#F59E0B" />
                        </Box>
                      )}
                      <Box sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>
                        <NeonChip label={getPublicityLabel(image)} neon={PUBLICITY_NEON[getPublicity(image)] || alpha(NEON, 0.5)} />
                      </Box>

                      <Box
                        className="gallery-footer"
                        sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          zIndex: 2,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.75,
                          background: alpha(SURFACE_BASE, 0.94),
                          borderTop: `1px solid ${alpha(NEON, 0.1)}`,
                          backdropFilter: 'blur(8px)',
                          p: 1.25,
                          pt: 1,
                        }}
                      >
                        <Typography
                          title={getGalleryDisplayName(image)}
                          sx={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontWeight: 700,
                            fontSize: '0.82rem',
                            color: GRID_TITLE_TEXT,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            lineHeight: 1.3,
                          }}
                        >
                          {getGalleryDisplayName(image)}
                        </Typography>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                          <NeonChip
                            label={isAchievementImage(image) ? 'Achievement' : 'Regular'}
                            neon={isAchievementImage(image) ? '#F59E0B' : alpha(NEON, 0.4)}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: 0.15,
                            pt: 0.5,
                            borderTop: `1px solid ${alpha(NEON, 0.08)}`,
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ActionBtn title="View gallery" icon={VisibilityIcon} color={ACTION_VIEW} onClick={() => navigate(`/galleries/${image.id}`)} />
                          {canAddPicturesToPrivate(image) && <ActionBtn title="Add Pictures" icon={AddPhotoIcon} color={NEON2} onClick={() => handleAddPictures(image)} />}
                          {canEditGallery(currentUser) && <ActionBtn title="Edit Gallery" icon={EditIcon} color={ACTION_EDIT} onClick={() => navigate(`/galleries/edit/${image.id}`, { state: { from: '/galleries' } })} />}
                          {canDeleteGallery(currentUser) && <ActionBtn title="Delete Gallery" icon={DeleteIcon} color={ACTION_DELETE} onClick={() => handleDelete(image)} />}
                        </Box>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ ...glassCardSx, mt: 3, overflow: 'hidden' }}>
              <TableListPagination
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={filteredImages.length}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[8, 12, 24, 48]}
              />
            </Box>
          </>
        )
      )}

      {/* ── List View ── */}
      {viewMode === 'list' && (
        <TableList
          columns={galleryColumns}
          data={filteredImages}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 12, 25, 50]}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          {...bulkTableProps(GALLERY_BULK_CONFIG, currentUser, selectedIds, setSelectedIds)}
          loading={loading}
          streamLabel="GALLERY_STREAM"
          emptyState={{
            icon: <GalleryIcon />,
            title: searchTerm || Object.keys(filters).length > 0 ? 'No galleries match your filters' : 'No galleries yet',
            description: searchTerm || Object.keys(filters).length > 0
              ? 'Try adjusting your search or filter criteria.'
              : 'Start by adding a new gallery.',
          }}
          onRowClick={(image) => navigate(`/galleries/${image.id}`)}
        />
      )}

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        variant="delete"
        itemType="gallery item"
        itemName={getGalleryDisplayName(selectedImage)}
        onConfirm={confirmDelete}
        confirmLabel="Delete Gallery"
      />

      {/* ── Add Pictures Dialog ── */}
      <Dialog
        open={addPicturesDialogOpen}
        onClose={closeAddPicturesDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          elevation: 0,
          onDragOver: (e) => e.preventDefault(),
          sx: {
            borderRadius: '20px',
            background: SURFACE_BASE,
            border: `1px solid ${alpha(NEON, 0.14)}`,
            boxShadow: `0 32px 80px ${alpha('#000', 0.65)}`,
            overflow: 'hidden',
          },
        }}
        BackdropProps={{
          sx: { background: 'rgba(3,9,18,0.75)', backdropFilter: 'blur(6px)' },
        }}
      >
        {/* ── Header ── */}
        <DialogTitle
          sx={{
            p: 0,
            borderBottom: `1px solid ${alpha(NEON, 0.1)}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2.5,
              py: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: '10px',
                  background: alpha(NEON, 0.1),
                  border: `1px solid ${alpha(NEON, 0.22)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <AddPhotoIcon sx={{ fontSize: 18, color: NEON }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    color: alpha('#F1F5F9', 0.95),
                    lineHeight: 1.2,
                  }}
                >
                  Add pictures
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: '0.75rem',
                    color: alpha('#94A3B8', 0.85),
                    mt: 0.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 280,
                  }}
                >
                  {getGalleryDisplayName(selectedGalleryForPics)}
                </Typography>
              </Box>
            </Box>
      
            <Tooltip title="Close" arrow>
              <IconButton
                onClick={closeAddPicturesDialog}
                size="small"
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: '8px',
                  color: alpha('#94A3B8', 0.7),
                  '&:hover': {
                    background: alpha('#fff', 0.06),
                    color: alpha('#F1F5F9', 0.9),
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogTitle>
      
        {/* ── Body ── */}
        <DialogContent
          sx={{ p: 2.5 }}
          onDragOver={(e) => e.preventDefault()}
        >
      
          {/* Drop zone — always visible so users can add more */}
          <Box
            component="label"
            htmlFor="picture-upload-new"
            onDragEnter={handleAddPicturesDragOver}
            onDragOver={handleAddPicturesDragOver}
            onDragLeave={handleAddPicturesDragLeave}
            onDrop={handleAddPicturesDrop}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              border: `1.5px dashed ${alpha(NEON, addPicturesDragging ? 0.65 : 0.28)}`,
              borderRadius: '14px',
              py: picturePreviews.length > 0 ? 2 : 3.5,
              px: 2,
              textAlign: 'center',
              background: addPicturesDragging ? alpha(NEON, 0.06) : alpha(SURFACE_UP, 0.35),
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              transform: addPicturesDragging ? 'scale(1.01)' : 'none',
              '&:hover': {
                borderColor: alpha(NEON, 0.55),
                background: alpha(NEON, 0.04),
              },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '50%',
                background: alpha(NEON, 0.1),
                border: `1px solid ${alpha(NEON, 0.2)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 0.5,
              }}
            >
              <CloudUploadIcon sx={{ fontSize: 20, color: NEON }} />
            </Box>
            <Typography
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                fontSize: '0.875rem',
                color: alpha('#E2E8F0', 0.9),
              }}
            >
              {picturePreviews.length > 0 ? 'Drop more images or click to browse' : 'Drop images here or click to browse'}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '0.72rem',
                color: alpha(NEON, 0.4),
              }}
            >
              JPEG · PNG · WebP · multiple files supported
            </Typography>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handlePictureFileChange}
              style={{ display: 'none' }}
              id="picture-upload-new"
            />
          </Box>
      
          {/* Preview grid */}
          {picturePreviews.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {/* Row header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 1.25,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 600,
                    fontSize: '0.8rem',
                    color: alpha('#CBD5E1', 0.85),
                  }}
                >
                  Selected
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: '0.7rem',
                      color: alpha(NEON, 0.7),
                      letterSpacing: 0.5,
                    }}
                  >
                    {picturePreviews.length} FILE{picturePreviews.length !== 1 ? 'S' : ''}
                  </Typography>
                  <Box
                    sx={{
                      width: 1,
                      height: 12,
                      background: alpha(NEON, 0.15),
                    }}
                  />
                  {/* Total size */}
                  <Typography
                    sx={{
                      fontFamily: "'DM Mono', monospace",
                      fontSize: '0.7rem',
                      color: alpha('#94A3B8', 0.7),
                    }}
                  >
                    {(
                      picturePreviews.reduce((acc, p) => acc + (p.file?.size || 0), 0) /
                      1048576
                    ).toFixed(1)}{' '}
                    MB
                  </Typography>
                </Box>
              </Box>
      
              {/* Grid */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: 1,
                  maxHeight: 240,
                  overflowY: 'auto',
                  pr: 0.5,
                  '&::-webkit-scrollbar': { width: '4px' },
                  '&::-webkit-scrollbar-track': { background: 'transparent' },
                  '&::-webkit-scrollbar-thumb': {
                    background: alpha(NEON, 0.2),
                    borderRadius: '4px',
                  },
                }}
              >
                {picturePreviews.map((preview, index) => (
                  <Box
                    key={`${preview.file?.name ?? 'img'}-${index}`}
                    sx={{
                      position: 'relative',
                      aspectRatio: '1',
                      borderRadius: '10px',
                      overflow: 'hidden',
                      border: `1px solid ${alpha(NEON, 0.12)}`,
                      background: alpha(SURFACE_UP, 0.6),
                      '&:hover .remove-btn': { opacity: 1 },
                      '&:hover img': { transform: 'scale(1.05)' },
                    }}
                  >
                    <Box
                      component="img"
                      src={preview.url}
                      alt={preview.file?.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                        transition: 'transform 0.25s ease',
                      }}
                    />
      
                    {/* Remove button */}
                    <Tooltip title="Remove" arrow>
                      <IconButton
                        className="remove-btn"
                        onClick={() => removePendingPicture(index)}
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          width: 22,
                          height: 22,
                          background: 'rgba(15,15,20,0.75)',
                          color: '#fff',
                          opacity: 0,
                          transition: 'opacity 0.18s ease',
                          backdropFilter: 'blur(4px)',
                          border: `1px solid ${alpha('#fff', 0.15)}`,
                          '&:hover': {
                            background: alpha('#EF4444', 0.9),
                          },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 11 }} />
                      </IconButton>
                    </Tooltip>
      
                    {/* Filename bar */}
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        px: 0.75,
                        py: 0.5,
                        background:
                          'linear-gradient(to top, rgba(0,0,0,0.78), transparent)',
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: '0.58rem',
                          color: alpha('#E2E8F0', 0.88),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {preview.file?.name}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
      
        {/* ── Footer ── */}
        <DialogActions
          sx={{
            px: 2.5,
            py: 2,
            gap: 1,
            borderTop: `1px solid ${alpha(NEON, 0.09)}`,
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {/* Left: size summary */}
          <Typography
            sx={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '0.75rem',
              color: alpha('#64748B', 0.9),
            }}
          >
            {picturePreviews.length > 0
              ? `${picturePreviews.length} file${picturePreviews.length !== 1 ? 's' : ''} ready`
              : 'No files selected'}
          </Typography>
      
          {/* Right: actions */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={closeAddPicturesDialog}
              disabled={uploadLoading}
              sx={{
                borderRadius: '10px',
                px: 2.5,
                py: 0.85,
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                fontSize: '0.82rem',
                color: alpha('#94A3B8', 0.85),
                background: 'transparent',
                border: `1px solid ${alpha(NEON, 0.15)}`,
                '&:hover': {
                  background: alpha(NEON, 0.05),
                  borderColor: alpha(NEON, 0.3),
                  color: alpha('#E2E8F0', 0.9),
                },
                '&.Mui-disabled': { opacity: 0.35 },
              }}
            >
              Cancel
            </Button>
      
            <Button
              onClick={handleUploadPictures}
              variant="contained"
              disabled={newPictures.length === 0 || uploadLoading}
              startIcon={
                uploadLoading ? null : <CloudUploadIcon sx={{ fontSize: '16px !important' }} />
              }
              sx={{
                borderRadius: '10px',
                px: 2.5,
                py: 0.85,
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 700,
                fontSize: '0.82rem',
                background: alpha(NEON, 0.15),
                border: `1px solid ${alpha(NEON, 0.35)}`,
                color: NEON,
                boxShadow: 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  background: alpha(NEON, 0.22),
                  borderColor: alpha(NEON, 0.55),
                  color: '#fff',
                  boxShadow: `0 0 18px ${alpha(NEON, 0.25)}`,
                },
                '&.Mui-disabled': {
                  background: alpha(SURFACE_UP, 0.5),
                  color: alpha(NEON, 0.22),
                  borderColor: alpha(NEON, 0.1),
                  boxShadow: 'none',
                },
              }}
            >
              {uploadLoading ? (
                <CircularProgress size={16} sx={{ color: alpha(NEON, 0.7) }} />
              ) : (
                `Upload${newPictures.length > 0 ? ` ${newPictures.length} file${newPictures.length !== 1 ? 's' : ''}` : ''}`
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GalleryList;
